import { Building2, Network as NetworkIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Tenant switcher, prefixed by the instance name.
 *
 * The network is a STATIC LABEL, not a selector: each network gets its own
 * deployment, so an instance hosts exactly one and there is nothing to switch
 * between. Picking a tenant is what changes the session scope and flushes the
 * query cache (see TenantProvider); no request ever carries a network id.
 *
 * Scoping comes from the server: full-access principals get every tenant from
 * the list endpoint, everyone else only theirs — so this component needs no
 * permission awareness of its own.
 */
export function TenancySwitcher() {
  const { instanceNetwork, tenants, selectedTenantId, setSelectedTenantId, isLoading } = useTenant()

  const tenantValue =
    selectedTenantId && tenants.some((t) => t.id === selectedTenantId)
      ? selectedTenantId
      : undefined

  return (
    <div className='inline-flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5 shadow-sm'>
      <NetworkIcon className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
      <span
        className='max-w-[12rem] truncate text-sm font-medium text-foreground'
        title={instanceNetwork?.name ?? undefined}
      >
        {instanceNetwork?.name ?? (isLoading ? 'Loading…' : '—')}
      </span>
      <div className='h-4 w-px bg-border' />
      <Building2 className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
      <Select
        value={tenantValue}
        onValueChange={setSelectedTenantId}
        disabled={isLoading || tenants.length === 0}
      >
        <SelectTrigger
          aria-label='Select tenant'
          className='h-auto w-auto gap-1.5 border-0 bg-transparent p-0 text-sm font-medium shadow-none focus:ring-0'
        >
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Tenant'} />
        </SelectTrigger>
        <SelectContent>
          {tenants.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
